import React, { useState } from 'react';
import { Form, Row, Col, Button } from 'react-bootstrap';
import Search from '../../assets/images/svg/search.svg';

const inputStyle = {
    height: '46px',
    border: '1px solid #E2E8F0',
    backgroundColor: '#FFFFFF',
    borderRadius: '6px',
};

const labelStyle = {
    color: '#4A5568',
    fontWeight: '500',
    fontSize: '16px',
};

export const MAX_AUDIO_TRACKS = 19;

const AudioPlaylistPicker = ({
    audioArtifacts,
    searchValue,
    onSearchChange,
    onFetchArtifacts,
    artifactsFetched,
    selected,
    onChange,
}) => {
    const [dragIndex, setDragIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    const normalizedSearch = (searchValue || '').toLowerCase();
    const filteredResults = audioArtifacts
        .filter((r) => (r.title || '').toLowerCase().includes(normalizedSearch))
        .filter((r) => !selected.some((s) => s.artifact_id === r.artifact_id));

    const atMax = selected.length >= MAX_AUDIO_TRACKS;

    const addTrack = (artifact) => {
        if (atMax) return;
        if (selected.some((s) => s.artifact_id === artifact.artifact_id)) return;
        onChange([...selected, { artifact_id: artifact.artifact_id, title: artifact.title }]);
        onSearchChange('');
    };

    const removeTrack = (id) => {
        onChange(selected.filter((s) => s.artifact_id !== id));
    };

    const moveTrack = (from, to) => {
        if (from === to || to < 0 || to >= selected.length) return;
        const next = [...selected];
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        onChange(next);
    };

    const handleDragStart = (idx) => setDragIndex(idx);
    const handleDragOver = (e, idx) => {
        e.preventDefault();
        if (dragOverIndex !== idx) setDragOverIndex(idx);
    };
    const handleDrop = (idx) => {
        if (dragIndex !== null) moveTrack(dragIndex, idx);
        setDragIndex(null);
        setDragOverIndex(null);
    };
    const handleDragEnd = () => {
        setDragIndex(null);
        setDragOverIndex(null);
    };

    const handleSearchInput = (v) => {
        onSearchChange(v);
        if (!artifactsFetched && onFetchArtifacts) onFetchArtifacts();
    };

    return (
        <Row className='mb-3'>
            <Col md={5}>
                <Form.Group>
                    <Form.Label className='mb-2' style={labelStyle}>
                        Audio Artifact <span className='text-danger'>*</span>
                        <span style={{ fontSize: '12px', color: '#A0AEC0', fontWeight: 400, marginLeft: 8 }}>
                            ({selected.length}/{MAX_AUDIO_TRACKS})
                        </span>
                    </Form.Label>
                    <div className='position-relative'>
                        <Form.Control
                            type='text'
                            placeholder={atMax ? 'Maximum 19 tracks reached' : 'Search Audio Artifact...'}
                            value={searchValue}
                            onChange={(e) => handleSearchInput(e.target.value)}
                            onFocus={() => { if (!artifactsFetched && onFetchArtifacts) onFetchArtifacts(); }}
                            disabled={atMax}
                            style={{ ...inputStyle, paddingRight: '35px' }}
                        />
                        <img
                            src={Search}
                            alt='search'
                            className='position-absolute'
                            style={{
                                right: '10px', top: '50%', transform: 'translateY(-50%)',
                                width: '16px', height: '16px', pointerEvents: 'none',
                            }}
                        />
                        {searchValue && searchValue.length > 0 && !atMax && (
                            <div
                                className='position-absolute w-100 bg-white border rounded mt-1 shadow-sm'
                                style={{ zIndex: 1000, maxHeight: '220px', overflowY: 'auto' }}
                            >
                                {filteredResults.map((result) => (
                                    <div
                                        key={result.artifact_id}
                                        className='p-2'
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => addTrack(result)}
                                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F3F4F6'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                                    >
                                        {result.title}
                                    </div>
                                ))}
                                {filteredResults.length === 0 && (
                                    <div className='p-2 text-muted text-center'>No results</div>
                                )}
                            </div>
                        )}
                    </div>
                </Form.Group>
            </Col>
            <Col md={7}>
                <Form.Group>
                    <Form.Label className='mb-2' style={labelStyle}>
                        Playlist Order
                        <span style={{ fontSize: '12px', color: '#A0AEC0', fontWeight: 400, marginLeft: 8 }}>
                            (drag to reorder — plays top to bottom)
                        </span>
                    </Form.Label>
                    <div
                        style={{
                            minHeight: '80px',
                            border: '1px solid #E2E8F0',
                            backgroundColor: '#FAFAFA',
                            borderRadius: '6px',
                            padding: '10px',
                        }}
                    >
                        {selected.length === 0 && (
                            <span style={{ color: '#A0AEC0', fontSize: '13px' }}>No tracks selected</span>
                        )}
                        {selected.map((item, idx) => (
                            <div
                                key={item.artifact_id}
                                draggable
                                onDragStart={() => handleDragStart(idx)}
                                onDragOver={(e) => handleDragOver(e, idx)}
                                onDrop={() => handleDrop(idx)}
                                onDragEnd={handleDragEnd}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: '8px 10px',
                                    marginBottom: '6px',
                                    background: '#FFFFFF',
                                    border: `1px solid ${dragOverIndex === idx ? '#6366F1' : '#E2E8F0'}`,
                                    borderRadius: '6px',
                                    cursor: 'grab',
                                    opacity: dragIndex === idx ? 0.4 : 1,
                                }}
                            >
                                <span style={{ color: '#A0AEC0', cursor: 'grab', fontSize: '16px', userSelect: 'none' }} title='Drag to reorder'>
                                    ⋮⋮
                                </span>
                                <span
                                    style={{
                                        minWidth: '24px', height: '24px', borderRadius: '50%',
                                        background: '#6366F1', color: 'white', fontSize: '12px',
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600,
                                    }}
                                >
                                    {idx + 1}
                                </span>
                                <span style={{ flex: 1, fontSize: '14px', color: '#2D3748' }}>{item.title || item.artifact_id}</span>
                                <Button
                                    variant='outline-secondary' size='sm'
                                    onClick={() => moveTrack(idx, idx - 1)}
                                    disabled={idx === 0}
                                    style={{ padding: '2px 8px' }}
                                    title='Move up'
                                >↑</Button>
                                <Button
                                    variant='outline-secondary' size='sm'
                                    onClick={() => moveTrack(idx, idx + 1)}
                                    disabled={idx === selected.length - 1}
                                    style={{ padding: '2px 8px' }}
                                    title='Move down'
                                >↓</Button>
                                <Button
                                    variant='outline-danger' size='sm'
                                    onClick={() => removeTrack(item.artifact_id)}
                                    style={{ padding: '2px 8px' }}
                                    title='Remove'
                                >×</Button>
                            </div>
                        ))}
                    </div>
                </Form.Group>
            </Col>
        </Row>
    );
};

export default AudioPlaylistPicker;
